import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, RefreshCw, PackageOpen, X, Recycle } from "lucide-react";

/**
 * Dialog shown after a user marks a container as consumed or discarded.
 * Three choices:
 *   1. Log new food → jump into the full Add Container screen with the box's
 *      qr/box_number/last-item context pre-filled (same URL shape as the
 *      empty-box scan flow, so AddContainer.jsx already handles it).
 *   2. Reset to fresh → refilling with the same food; container stays active.
 *   3. Leave empty → close the dialog.
 */
export const PostStatusDialog = ({ open, container, status, onClose, onReset }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const close = () => onClose?.();

  const logNewFood = () => {
    if (!container) return;
    const params = new URLSearchParams({ qr: container.qr_code });
    if (container.box_number) params.set("box", String(container.box_number));
    if (container.name) params.set("last", container.name);
    if (status) params.set("last_status", status);
    close();
    navigate(`/add?${params.toString()}`);
  };

  return (
    <AnimatePresence>
      {open && container && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-md flex items-end sm:items-center justify-center"
          onClick={close}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="post-status-dialog"
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-6 sm:m-4"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#DCEDF0] flex items-center justify-center">
                  <Check className="w-5 h-5 text-[#1F5566]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#277A82]">
                    Marked as {status}
                  </div>
                  <div className="font-heading text-lg font-semibold text-[#2D3142]">
                    What&apos;s next for this container?
                  </div>
                </div>
              </div>
              <button onClick={close} data-testid="post-status-close" className="text-[#8A93A6] p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={logNewFood}
                data-testid="post-status-reuse-btn"
                className="w-full tup-card p-4 text-left hover:bg-[#F5F3ED] transition-colors flex items-start gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-[#DCEDF0] flex items-center justify-center shrink-0">
                  <RefreshCw className="w-4 h-4 text-[#1F5566]" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-[#2D3142]">Log new food</div>
                  <div className="text-sm text-[#4F5D75] mt-0.5">
                    Reuse this same container with a new dish. Keeps the QR code.
                  </div>
                </div>
              </button>

              {onReset && (
                <button
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await onReset();
                      close();
                    } finally {
                      setBusy(false);
                    }
                  }}
                  disabled={busy}
                  data-testid="post-status-reset-btn"
                  className="w-full tup-card p-4 text-left hover:bg-[#F5F3ED] transition-colors flex items-start gap-3 disabled:opacity-60"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E8F0E5] flex items-center justify-center shrink-0">
                    <Recycle className="w-4 h-4 text-[#3E6B4E]" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-[#2D3142]">Reset to fresh</div>
                    <div className="text-sm text-[#4F5D75] mt-0.5">
                      Refilling with the same food — keep the container active in your fridge.
                    </div>
                  </div>
                </button>
              )}

              <button
                onClick={close}
                data-testid="post-status-empty-btn"
                className="w-full tup-card p-4 text-left hover:bg-[#F5F3ED] transition-colors flex items-start gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-[#F5F3ED] flex items-center justify-center shrink-0">
                  <PackageOpen className="w-4 h-4 text-[#4F5D75]" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-[#2D3142]">Leave empty</div>
                  <div className="text-sm text-[#4F5D75] mt-0.5">
                    Container is clean and empty — nothing to log right now.
                  </div>
                </div>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
